import { canManageReport } from "@/profitability/access";
import { inputTitle } from "@/profitability/missing-value";
import { shortPeriod } from "@/profitability/new-run/run-period";
import type { RecordObject } from "@/profitability/records/objects";
import { profitabilityNewRunPath } from "@/profitability/routes";
import type { ProfitabilityReport, ReportDates } from "@/profitability/types";
import type { OverviewInsight } from "./types";

/**
 * Where a "no Labor cost yet" line sends someone who can run the template:
 * the upload page for the period, its dates set, the input the cost comes
 * from marked and the period's other files kept. "Upload Completed jobs
 * export" when the cost's column names its input (`source`), else "Add
 * files for Oct 12–18". Null for any other line, or for someone who may
 * only view the template.
 */
export const missingUpload = ({
  insight,
  report,
  objects,
  run,
}: {
  insight: OverviewInsight;
  report: ProfitabilityReport;
  objects: RecordObject[];
  run: { id: string; dates: ReportDates | null };
}) => {
  if (insight.kind !== "missing") return null;
  if (!canManageReport(report) || !report.published || report.archived_at)
    return null;
  const facts = insight.facts ?? {};
  // A line stored before it named the input still finds it on the column.
  const source =
    facts.source ??
    objects
      .find((object) => object.dataset.id === facts.dataset)
      ?.dataset.columns.find((column) => column.key === facts.cost)?.source ??
    null;
  const title = inputTitle(report.inputs, source);
  const { dates } = run;
  return {
    label: title
      ? `Upload ${title}`
      : dates
        ? `Add files for ${shortPeriod(dates)}`
        : "Add files",
    to: profitabilityNewRunPath(
      report.id,
      dates && { ...dates, input: title ? source : null, from: run.id },
    ),
  };
};
